/**
 * "What your team will see": the short page for managers (`/training/managers`).
 * Plain language, honest about what is not built, no rule codes. The case page in the
 * three steps a sales person works through, so the manager can tell the team the same.
 * Round 3 (12 Oct 2026): the three-step strip as a figure, Simplified Technical English.
 */

import type { ManagerBrief } from "@/features/training/types";

export const MANAGER_BRIEF: ManagerBrief = {
  title: "What your team will see",
  subtitle:
    "For managers: the three steps of a case, what I2Q prepares, what it does not do yet, and what to tell your sales people. About 5 minutes.",
  minutes: 5,
  sections: [
    {
      id: "shadow-mode",
      title: "Shadow mode",
      body: [
        {
          kind: "text",
          text: "I2Q reads a copy of the shared mailboxes. It prepares every inquiry: the customer, the lines, the prices and the reply. Nothing is sent to a customer and nothing is written to F&O. Your sales people still reply from Outlook and make the quotation in F&O, as today.",
        },
        {
          kind: "note",
          tone: "info",
          title: "What changes for the team",
          text: "Nothing in the way they work with customers. I2Q is a second screen that has the case ready when they open it.",
        },
      ],
    },
    {
      id: "three-steps",
      title: "The three steps of a case",
      body: [
        {
          kind: "text",
          text: "A case page has three steps, in this order, and nothing else expanded. After the quick start, a sales person opens a case and knows within seconds what the customer wants, what I2Q proposes and what to do.",
        },
        { kind: "figure", figure: "three-steps" },
        {
          kind: "steps",
          steps: [
            "Read the mail. The mail is a compact card at the top of the page, with the customer line in it. Confirmed with a green person means I2Q is sure. Confirm with the name is one click, after the sales person reads the evidence under Why. Find customer opens the search. The attachments are chips with their icon. Long text is folded to 6 lines with Read all. A follow-up or a reminder is one line under the subject.",
            "Check the draft. The draft is a table in the middle of the page. Each row is one requested line with an icon and a word. A green check means matched: nothing to do. A blue question mark means confirm: one click. An orange box with a magnifier means pick: pick one from the list. A grey ban sign means no item: tell the customer. The rows that need the person sit on top under Needs you, then Matched, then Nothing to quote folded. The quantity, the price with its band icon and the lead time stand on every row. The row button sits at the right end. The details of a row open only when the word is not matched or a price looks wrong.",
            "Do the one action. One big button in the bar at the bottom names the next step. It changes as the person works: Confirm, Pick 2 items, Check, Prepare the reply, Copy the reply, Mark done and next. Nothing to do means: press it and go to the next case. Nothing is sent from there.",
          ],
        },
        {
          kind: "text",
          text: "Everything else sits one click away. Take, Set status and Give feedback sit under More. The checks, the quotation, the order system, the documents and the history sit under Details. The queue follows the same idea: one row per case, with one action word and the same icon in front of it. It shows today's mail by default. The backlog chips open older work. The rest sits under Filters.",
        },
      ],
    },
    {
      id: "prepares",
      title: "What I2Q prepares",
      body: [
        {
          kind: "list",
          items: [
            "The customer: certain when F&O knows the sender, else a suggestion with the evidence one click away under Why.",
            "The lines: each item number and quantity found in the mail, matched to the F&O item, with the reason in the row's Details.",
            "The prices: from the price list or the price group of the customer. A manager approves a price below the limit.",
            "The reply: in the language of the customer, with the lines and the open points, in a card above the one action.",
            "The queue: every inquiry of the area, who has it, how long it waits and what it needs from a person, as one word.",
          ],
        },
      ],
    },
    {
      id: "not-yet",
      title: "What I2Q does not do yet",
      body: [
        {
          kind: "list",
          items: [
            "It does not send anything and does not write the quotation into F&O.",
            "It does not read the replies of every area yet. An inquiry with a reply can stay open in I2Q until somebody closes it.",
            "Delivery dates: where the area has no lead times in F&O, the row shows none.",
            "The end customer behind an agent is a suggestion, never certain.",
            "I2Q does not assign a case to the colleague who owns the customer. The first click of a sales person takes it.",
          ],
        },
        {
          kind: "text",
          text: "Each country guide under Rules and countries has its own list of what is not there yet for that area.",
        },
      ],
    },
    {
      id: "go-live-bar",
      title: "The go-live bar",
      body: [
        {
          kind: "text",
          text: "An area goes live only when two figures hold. When I2Q is sure, it is right at least 99.5 % of the time, on at least 100 lines. At least 75 % of the lines are done without a person. The dashboard Summary shows every area against this bar, each on its own. Until then everything stays in shadow mode.",
        },
      ],
    },
    {
      id: "feedback",
      title: "Where feedback goes",
      body: [
        {
          kind: "text",
          text: "Sales people give feedback from Give feedback on any page, under More on a case, or from Was this right? in a row's Details. It lands in Feedback triage, by area, where you and IT read it. A correction of a row teaches I2Q an alias. A manager approves the alias under Learned corrections. A manager approves a held price in the checks under Details, with Approve price.",
        },
      ],
    },
    {
      id: "tell-your-team",
      title: "What to tell your team",
      body: [
        {
          kind: "steps",
          steps: [
            "Open the queue once a day. Read the Action column. Open the backlog chips for older work.",
            "Read the mail. Confirm the customer only with the evidence under Why.",
            "Check the draft: the table in the middle, an icon and a word per row. The lines that need you are on top. Compare the quantity, the unit and the item with the customer's words.",
            "Do the one action. Copy the reply into Outlook. Correct it there and send it.",
            "Set the status under More. Then the queue is right for everyone.",
          ],
        },
        {
          kind: "text",
          text: "The quick start takes 5 minutes and covers exactly these steps. The full training takes about 23 minutes.",
        },
      ],
    },
  ],
  teamSteps: [
    "Read the mail: the customer line says Confirmed, Confirm or Find customer.",
    "Check the draft: an icon and a word per row, matched, confirm, pick or no item. Work the lines that need you on top first.",
    "Do the one action: the big button, from Confirm to Copy the reply.",
    "Copy the reply into Outlook. Send it from Outlook. I2Q sends nothing.",
    "Set the status under More. Then the queue is right for everyone.",
  ],
};
